import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaDownload } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL;

const PaymentHistoryManagement = () => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('userID');
  const [userIdToPhoto, setUserIdToPhoto] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [newRecord, setNewRecord] = useState({
    userID: '',
    income: '',
    point: '',
    notes: ''
  });

  const resolveAvatar = (val) => {
    if (!val) return 'https://placehold.co/80x80/png';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      // common shapes: { photodatas }, { url }
      return val.photodatas || val.url || 'https://placehold.co/80x80/png';
    }
    return 'https://placehold.co/80x80/png';
  };

  useEffect(() => {
    fetchPaymentHistory();
    fetchAdminUsersForPhotos();
  }, []);

  const fetchAdminUsersForPhotos = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const map = {};
      (res.data || []).forEach(u => {
        const photo = (u?.photoLink?.photodatas) || (u?.photoLink) || 'https://placehold.co/600x400/png';
        if (u?.userID) {
          map[u.userID] = photo;
        }
      });
      setUserIdToPhoto(map);
    } catch (e) {
      // ignore - fallback will be used
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/payment-history`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setPaymentHistory(response.data.data || []);
    } catch (error) {
      console.error('Error fetching payment history:', error);
      alert('入金履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = async () => {
    try {
      if (!newRecord.userID || !newRecord.income || !newRecord.point) {
        alert('ユーザーID、入金金額、ポイント数は必須項目です');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/payment-history`, newRecord, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      alert('入金履歴を追加しました');
      setShowAddModal(false);
      setNewRecord({ userID: '', income: '', point: '', notes: '' });
      fetchPaymentHistory();
    } catch (error) {
      console.error('Error adding payment history:', error);
      alert('入金履歴の追加に失敗しました: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEditRecord = async () => {
    try {
      if (!selectedRecord.income || !selectedRecord.point) {
        alert('入金金額、ポイント数は必須項目です');
        return;
      }

      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/payment-history/${selectedRecord._id}`, {
        income: selectedRecord.income,
        point: selectedRecord.point,
        notes: selectedRecord.notes
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      alert('入金履歴を更新しました');
      setShowEditModal(false);
      setSelectedRecord(null);
      fetchPaymentHistory();
    } catch (error) {
      console.error('Error updating payment history:', error);
      alert('入金履歴の更新に失敗しました: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('この入金履歴を削除しますか？この操作は取り消せません。')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/payment-history/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      alert('入金履歴を削除しました');
      fetchPaymentHistory();
    } catch (error) {
      console.error('Error deleting payment history:', error);
      alert('入金履歴の削除に失敗しました');
    }
  };

  const openEditModal = (record) => {
    setSelectedRecord({ ...record });
    setShowEditModal(true);
  };

  const filteredHistory = paymentHistory.filter(record => {
    const searchLower = searchTerm.toLowerCase();
    switch (searchField) {
      case 'userID':
        return record.userID.toLowerCase().includes(searchLower);
      case 'username':
        return record.username.toLowerCase().includes(searchLower);
      case 'paymentAmount':
        return record.paymentAmount.toString().includes(searchLower);
      case 'points':
        return record.points.toString().includes(searchLower);
      default:
        return true;
    }
  });

  // Export helpers
  const pad2 = (n) => String(n).padStart(2, '0');
  const generateTimestamp = () => {
    const d = new Date();
    return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}_${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`;
  };

  const escapeCsv = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (/[",\n\r]/.test(str)) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  const toCsv = (rows) => {
    const header = ['ユーザーID', 'ユーザー名', '入金金額', 'ポイント数', '日時', '備考'];
    const lines = rows.map(r => [
      r.userID,
      r.username,
      r.paymentAmount,
      r.points,
      new Date(r.date).toLocaleString('ja-JP'),
      r.notes || ''
    ].map(escapeCsv).join(','));
    return header.map(escapeCsv).join(',') + '\n' + lines.join('\n');
  };

  const sanitizeTsv = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).replace(/\r?\n/g, ' ').replace(/\t/g, ' ');
  };

  const toTsv = (rows) => {
    const header = ['ユーザーID', 'ユーザー名', '入金金額', 'ポイント数', '日時', '備考'];
    const lines = rows.map(r => [
      r.userID,
      r.username,
      r.paymentAmount,
      r.points,
      new Date(r.date).toLocaleString('ja-JP'),
      r.notes || ''
    ].map(sanitizeTsv).join('\t'));
    return header.map(sanitizeTsv).join('\t') + '\n' + lines.join('\n');
  };

  const downloadText = (text, filename, mime) => {
    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([BOM, text], { type: `${mime};charset=utf-8;` });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleDownloadCsv = () => {
    if (!filteredHistory.length) {
      alert('エクスポート対象がありません');
      return;
    }
    const csv = toCsv(filteredHistory);
    downloadText(csv, `payment_history_${generateTimestamp()}.csv`, 'text/csv');
  };

  const handleDownloadTxt = () => {
    if (!filteredHistory.length) {
      alert('エクスポート対象がありません');
      return;
    }
    const tsv = toTsv(filteredHistory);
    downloadText(tsv, `payment_history_${generateTimestamp()}.txt`, 'text/plain');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>読み込み中...</div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ color: '#2c3e50', marginBottom: '30px' }}>入金履歴管理</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: '16px', color: '#7f8c8d' }}>
              総入金履歴数: <strong>{filteredHistory.length}</strong>件
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select
              value={searchField}
              onChange={(e) => setSearchField(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '5px'
              }}
            >
              <option value="userID">ユーザーID</option>
              <option value="username">ユーザー名</option>
              <option value="paymentAmount">入金金額</option>
              <option value="points">ポイント数</option>
            </select>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="検索..."
                style={{
                  padding: '8px 12px 8px 35px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  width: '200px'
                }}
              />
              <FaSearch style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#7f8c8d'
              }} />
            </div>
            <button
              onClick={handleDownloadCsv}
              style={{
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px'
              }}
            >
              <FaDownload /> CSVダウンロード
            </button>
            {/* <button
              onClick={handleDownloadTxt}
              style={{
                backgroundColor: '#8e44ad',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px'
              }}
            >
              <FaDownload /> TXTダウンロード
            </button> */}
            {/* <button
              onClick={() => setShowAddModal(true)}
              style={{
                backgroundColor: '#27ae60',
                color: 'white',
                border: 'none',
                padding: '10px 15px',
                borderRadius: '5px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '14px'
              }}
            >
              <FaPlus /> 入金履歴追加
            </button> */}
          </div>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'white',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#34495e', color: 'white' }}>
              <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>写真</th>
              <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>ユーザーID</th>
              <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>ユーザー名</th>
              <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>入金金額</th>
              <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>ポイント数</th>
              <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>日付時刻</th>
              <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>備考</th>
              <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredHistory.map((record) => (
              <tr key={record._id} style={{ borderBottom: '1px solid #ecf0f1', backgroundColor: 'white' }}>
                <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                  <img
                    src={userIdToPhoto[record.userID] || record?.userAvatar || 'https://placehold.co/600x400/png'}
                    alt={record.username}
                    style={{
                      width: '70px',
                      height: '70px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/600x400/png';
                    }}
                  />
                </td>
                <td style={{ padding: '15px', fontFamily: 'monospace', verticalAlign: 'middle' }}>{record.userID}</td>
                <td style={{ padding: '15px', fontWeight: 'bold', verticalAlign: 'middle' }}>{record.username}</td>
                <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                  <span style={{
                    backgroundColor: '#27ae60',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: 'bold'
                  }}>
                    ¥{record.paymentAmount.toLocaleString()}
                  </span>
                </td>
                <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                  <span style={{
                    backgroundColor: '#f39c12',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: 'bold'
                  }}>
                    {record.points.toLocaleString()}pt
                  </span>
                </td>
                <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                    {new Date(record.date).toLocaleDateString('ja-JP')}
                  </div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d' }}>
                    {new Date(record.date).toLocaleTimeString('ja-JP')}
                  </div>
                </td>
                <td style={{ padding: '15px', maxWidth: '200px', wordWrap: 'break-word', verticalAlign: 'middle' }}>
                  {record.notes || '-'}
                </td>
                <td style={{ padding: '15px', verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {/* <button
                      onClick={() => openEditModal(record)}
                      style={{
                        backgroundColor: '#3498db',
                        color: 'white',
                        border: 'none',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <FaEdit /> 編集
                    </button> */}
                    <button
                      onClick={() => handleDeleteRecord(record._id)}
                      style={{
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <FaTrash /> 削除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredHistory.length === 0 && (
        <div style={{ textAlign: 'center', padding: '50px', color: '#7f8c8d' }}>
          入金履歴がありません
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '500px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>
              入金履歴追加
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                ユーザーID: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                value={newRecord.userID}
                onChange={(e) => setNewRecord({...newRecord, userID: e.target.value})}
                placeholder="ユーザーIDを入力"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                入金金額: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="number"
                value={newRecord.income}
                onChange={(e) => setNewRecord({...newRecord, income: e.target.value})}
                placeholder="入金金額を入力"
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                ポイント数: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="number"
                value={newRecord.point}
                onChange={(e) => setNewRecord({...newRecord, point: e.target.value})}
                placeholder="ポイント数を入力"
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                備考:
              </label>
              <textarea
                value={newRecord.notes}
                onChange={(e) => setNewRecord({...newRecord, notes: e.target.value})}
                placeholder="備考を入力（任意）"
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewRecord({ userID: '', income: '', point: '', notes: '' });
                }}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleAddRecord}
                disabled={!newRecord.userID.trim() || !newRecord.income || !newRecord.point}
                style={{
                  backgroundColor: (!newRecord.userID.trim() || !newRecord.income || !newRecord.point) ? '#95a5a6' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (!newRecord.userID.trim() || !newRecord.income || !newRecord.point) ? 'not-allowed' : 'pointer'
                }}
              >
                追加
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedRecord && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            width: '500px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>
              入金履歴編集
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                ユーザーID:
              </label>
              <input
                type="text"
                value={selectedRecord.userID}
                disabled
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                  backgroundColor: '#f8f9fa'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                入金金額: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="number"
                value={selectedRecord.income}
                onChange={(e) => setSelectedRecord({...selectedRecord, income: e.target.value})}
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                ポイント数: <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="number"
                value={selectedRecord.point}
                onChange={(e) => setSelectedRecord({...selectedRecord, point: e.target.value})}
                min="0"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                備考:
              </label>
              <textarea
                value={selectedRecord.notes}
                onChange={(e) => setSelectedRecord({...selectedRecord, notes: e.target.value})}
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  fontSize: '16px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedRecord(null);
                }}
                style={{
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                キャンセル
              </button>
              <button
                onClick={handleEditRecord}
                disabled={!selectedRecord.income || !selectedRecord.point}
                style={{
                  backgroundColor: (!selectedRecord.income || !selectedRecord.point) ? '#95a5a6' : '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: (!selectedRecord.income || !selectedRecord.point) ? 'not-allowed' : 'pointer'
                }}
              >
                更新
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryManagement;
